import {
	Divider,
	Headline,
	IconButton,
	ProgressIndicator,
	Text,
	TextButton,
	TextLink,
} from '@oneaudi/unified-web-components';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { RegisterOrUpdateConsumptionFootnoteForDialog } from '~/app/compositions/content/calculation-info/components/consumption-footnote';
import { FsagMonthlyPriceRateDialog } from '~/app/compositions/content/calculation-info/components/fsag-finance-dialog';
import {
	ButtonsStyle,
	HeaderStyle,
	OverviewStyle,
	PriceDetailsStyle,
	WebcalcContainer,
} from '~/app/compositions/content/service-footer/components/price-details/price-details-style';
import { useAppState } from '~/app/contexts/app-context';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { useI18n } from '~/app/i18n/i18n';
import { AEM_MARKET_CONFIG, useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { StockCarEnabled, useStockCarDialog } from '~/app/pages/configurator/components/dialogs/stock-car-dialog';
import { useCtxConfigurator } from '~/app/pages/configurator/context/ctx-configurator';
import { PriceSectionStyle } from '~/app/pages/summary/dialogs/price-detail/components/price-section-style';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { getFilteredPriceParts } from '~/app/utils/FilteredPriceParts';
import isConsFinancingStage from '~/app/utils/is-cons-financing-stage';
import { FootnoteRenderer, useFootnotes } from '~/components/footnotes';
import { CAE_CONFIGUREDCAR_REMARK } from '~/components/footnotes/constants';
import { FootnoteReferenceNgc } from '~/components/footnotes/footnote-reference';
import { RenderHtml } from '~/components/text';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { useCarlineQuery } from '~/graphql/hooks/use-configured-car-queries';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';
import { state } from '~/storage';

export const PriceDetailsLegalGb = () => {
	const { setOpenPriceDetails } = useCtxConfigurator();

	return (
		<PriceDetailsStyle>
			<div className="close-button">
				<IconButton
					variant={'tertiary'}
					icon={'arrow-down'}
					size={'md'}
					onClick={() => setOpenPriceDetails(false)}
				/>
			</div>
			<div className="content">
				<Header />
				<Overview />
				<FootnoteRenderer />
				<Buttons />
			</div>
		</PriceDetailsStyle>
	);
};

const Header = () => {
	const { config } = useMarketConfig();
	const { catalog } = useConfigurator();
	const { getReference } = useFootnotes();
	const { i18n } = useI18n();

	if (config.showCaESummaryText && !config.hideConsumptionEmissionEngineName) {
		RegisterOrUpdateConsumptionFootnoteForDialog();
	}

	return (
		<HeaderStyle>
			<Headline tag="h2" variant={{ type: 'headline', order: '3', weight: 'normal' }}>
				{catalog.carInfo?.carline}
			</Headline>
			<Text tag="p" variant={{ type: 'copy', order: '1', style: 'normal' }}>
				{catalog.carInfo?.model}
				<FootnoteReferenceNgc
					reference={getReference(CAE_CONFIGUREDCAR_REMARK)}
					ariaLabel={i18n('ngc.a11y.footnotes.cae')}
				/>
			</Text>
		</HeaderStyle>
	);
};

const Overview = () => {
	const { catalog } = useConfigurator();
	const { config, market } = useMarketConfig();
	const { i18n, i18nKeys } = useI18n();
	const { formatCurrency, formatMonthlyRate } = useCurrencyFormatter();
	const vatConfig = useVATConfig();
	const { priceSummary } = useCatalog();
	const { openDialog, closeDialog, isDialogOpen } = useDialogs();
	const financialProducts = useAtomValue(state.financialProducts);

	const { priceDateRemark } = usePriceDetailRemarks();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const defaultProduct = useAtomValue(state.defaultFinancialProduct);
	const { financialCalculator, carIdentifier } = useConfigurator();
	const culture = `${AEM_MARKET_CONFIG.language}-${AEM_MARKET_CONFIG.market.toUpperCase()}`;
	const monthlyRate = catalog.priceSummary?.monthlyRate;
	const [preset] = useAtom(state.fsagFinancialCalculatorSettings);
	const { financingData } = useAppState();

	const product = preset ?? { '@ID': defaultProduct };

	// FSAG Summary - official financing content
	useEffect(() => {
		if (financingData?.loading) return;
		setIsLoading(true);
		if (!config.enableFinancingServices) {
			setIsLoading(false);
			return;
		}

		const request = {
			Request: {
				'@Domain': financingData?.financing?.meta?.externalRequestOptions['@Domain']
					? financingData?.financing.meta?.externalRequestOptions['@Domain']
					: config.financeDomain,
				'@Culture': culture,
				Vehicle: {
					...financingData?.financing.meta?.externalRequestOptions?.Vehicle,
				},
				Product: product,
			},
		};
		if (!financialCalculator) return;

		const interfaceElement = document.getElementById('interface');

		const calculator = isConsFinancingStage()
			? new financialCalculator({
					WEBCALC_API_URL: 'https://api.cons.webcalc.vwfs.io/webcalc-frontend-service',
					WEBCALC_API_STAGE: 'cons',
				})
			: new financialCalculator();

		calculator.getDefaultSummary(request, {
			ResultContainer: interfaceElement,
			ResponseCallback: () => {
				setIsLoading(false);
			},
			ErrorCallback: () => {
				setIsLoading(false);
				console.error('Financial error Callback triggered.');
			},
		});
	}, [
		financingData?.loading,
		config.enableFinancingServices,
		config.financeDomain,
		culture,
		JSON.stringify(financingData?.financing.meta?.externalRequestOptions?.Vehicle),
		JSON.stringify(financialCalculator),
		JSON.stringify(product),
		market,
	]);

	const carlineData = useCarlineQuery(carIdentifier, config.financingInput);
	const carType =
		carlineData?.carline?.configuredCar?.carlineStructureCarline?.vehicleType ||
		carlineData?.carline?.configuredCar?.technicalDataV2?.modelAttributes?.driveTrain;
	const priceDisclaimerText = useMemo(() => {
		if (config.enablePriceDisclaimer) {
			return carType
				? carType === 'BEV'
					? i18n('ngc.remarks.price.legalBEV')
					: i18n('ngc.remarks.price.legalICE')
				: undefined;
		}
		return null;
	}, [config.enablePriceDisclaimer, carType, i18n]);
	if (config.showCaESummaryText && !config.hideConsumptionEmissionEngineName) {
		RegisterOrUpdateConsumptionFootnoteForDialog();
	}

	const priceParts = getFilteredPriceParts(priceSummary, config);

	return (
		<OverviewStyle>
			<Headline tag="h2" variant={{ type: 'headline', order: '4', weight: 'bold' }}>
				{i18n('ngc.priceDetails.title')}
			</Headline>
			<div className="prices">
				{priceParts?.map((part) => (
					<PriceSectionFsag
						key={part.type}
						pricePartType={part.type}
						title={i18n(`ngc.priceDetails.${part.type}` as keyof typeof i18nKeys)}
						price={formatCurrency(part.price, vatConfig.priceParts[part.type])}
						disclaimer={null}
					/>
				))}
			</div>
			<Divider />
			<PriceSectionFsag
				pricePartType="total"
				title={i18n(`ngc.priceDetails.${config.uiPreferences.prices.totalPriceFrom}`)}
				price={formatCurrency(catalog.priceSummary?.totalPrice, vatConfig.totalPrice)}
				bold
				disclaimer={priceDisclaimerText}
			/>
			{catalog?.priceSummary?.monthlyRate &&
				catalog.priceSummary.monthlyRate.__typename === 'MonthlyRate' &&
				config.enableFinancingServices && (
					<div className="rates">
						{isLoading ? (
							<ProgressIndicator type={'infinite'} variant={'spinner'} />
						) : (
							<>
								<PriceSectionFsag
									pricePartType="totalRate"
									title={
										financialProducts.find((prod) => {
											return product && prod.key === product['@ID'];
										})?.val ?? i18n('ngc.priceDetails.privateLeasing')
									}
									price={formatMonthlyRate(catalog.priceSummary?.monthlyRate.rawValue)}
									bold
									disclaimer={null}
								/>
								<TextLink
									variant="standalone"
									size="sm"
									onClick={() => {
										closeDialog('priceDetails');
										appTrackingService.summaryClickEvent({
											eventAction: EventAction.InternalLink,
											eventName: `configurator (price details) - click to open configurator-financing-options-layer`,
											componentName: 'price-details-layer',
											label: i18n('ngc.actions.changeLeasingRate'),
										});
										openDialog('monthlyPriceRatesFsag');
									}}>
									{i18n('ngc.actions.changeLeasingRate')}
								</TextLink>
							</>
						)}
						<WebcalcContainer id="interface" />
					</div>
				)}
			{priceDateRemark && (
				<Text tag="p" variant={{ type: 'copy', order: '2', style: 'normal' }}>
					<RenderHtml html={priceDateRemark} />
				</Text>
			)}
			{monthlyRate && config.enableFinancingServices && (
				<div>
					<FsagMonthlyPriceRateDialog
						open={isDialogOpen('monthlyPriceRatesFsag')}
						onClose={() => closeDialog('monthlyPriceRatesFsag')}
					/>
				</div>
			)}
		</OverviewStyle>
	);
};

const Buttons = () => {
	const [, setCurrentView] = useAtom(state.currentView);
	const { i18n } = useI18n();
	const { config } = useMarketConfig();
	const { showStockCarDialog } = useStockCarDialog(config.stockCarConfig.geoFilter);
	const { setOpenPriceDetails } = useCtxConfigurator();

	const handleClickSummary = useCallback(() => {
		const body = document?.getElementsByTagName('body');
		body[0].style.overflow = 'auto';
		setOpenPriceDetails(false);
		setCurrentView('summary');
		appTrackingService.basketNavigationClick({
			label: i18n('ngc.actions.getYourCar'),
			targetUrl: '#summary',
			targetPage: 'summary',
		});
	}, [i18n, setCurrentView]);

	const handleClickStockCar = () => {
		setOpenPriceDetails(false);
		showStockCarDialog();
	};

	return (
		<ButtonsStyle>
			{StockCarEnabled() && (
				<TextButton
					className="main-cta-button"
					type={'button'}
					variant={'secondary'}
					onClick={handleClickStockCar}
					size={'sm'}
					isFullWidth>
					{i18n('ngc.stockcar.trigger')}
				</TextButton>
			)}
			<TextButton href={'#summary'} variant="primary" onClick={handleClickSummary} size="sm">
				{i18n('ngc.actions.getYourCar')}
			</TextButton>
		</ButtonsStyle>
	);
};

const PriceSectionFsag = ({
	pricePartType,
	title,
	price,
	bold,
	disclaimer,
}: {
	pricePartType: string;
	/** Translated title of this section.  */
	title: string;
	/** Formatted price of this part. */
	price: string | null | undefined;
	bold?: boolean;
	disclaimer: string | null | undefined;
}) => {
	const { i18n, i18nKeys } = useI18n();
	const { addFootnote, getReference } = useFootnotes();

	const i18nKey = `ngc.remarks.pricePart.${pricePartType}` as keyof typeof i18nKeys;

	if (i18nKey in i18nKeys) {
		addFootnote(i18nKey, i18n(i18nKey));
	}

	if (disclaimer) {
		addFootnote('p-disclaimer', disclaimer);
	}

	return (
		<PriceSectionStyle>
			<Text tag="p" variant={{ type: 'copy', order: '2', style: bold ? 'bold' : 'normal' }}>
				{title}

				<FootnoteReferenceNgc
					reference={getReference(i18nKey)}
					ariaLabel={i18n('ngc.a11y.footnotes.price.part', { part: title })}
				/>
				<FootnoteReferenceNgc
					reference={getReference('p-disclaimer')}
					ariaLabel={i18n('ngc.a11y.footnotes.price.disclaimer')}
				/>
			</Text>
			<Text tag="p" variant={{ type: 'copy', order: '2', style: bold ? 'bold' : 'normal' }}>
				{price}
			</Text>
		</PriceSectionStyle>
	);
};

/**
 * Returns the remark text to be shown in the bottom of the price details layer.
 */
function usePriceDetailRemarks(): {
	priceDateRemark: string | undefined | null;
} {
	const { priceSummary } = useCatalog();

	return {
		priceDateRemark: priceSummary?.priceDate?.text,
	};
}
